import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { ArrowUpRight, ShieldCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { HealthCopy, HealthOnboarding } from '@/frontend/features/Fde/dataHealth/types'
import { format, formatDate } from '@/frontend/features/Fde/dataHealth/types'

function statusText(onboarding: HealthOnboarding, copy: HealthCopy) {
  if (onboarding.status === 'atlas_assigned' && onboarding.atlas_finished) return copy.onboarding_atlas_done
  return copy[`onboarding_${onboarding.status}`] ?? ''
}

export default function OnboardingPanel({ enabled, onboarding, canManage, approvePath, copy }: {
  enabled: boolean; onboarding: HealthOnboarding | null; canManage: boolean; approvePath: string | null; copy: HealthCopy
}) {
  const [approving, setApproving] = useState(false)
  const approve = () => {
    if (!approvePath) return
    setApproving(true)
    router.post(approvePath, {}, { preserveScroll: true, onFinish: () => setApproving(false) })
  }

  return (
    <section aria-labelledby="proximos-passos" className="grid gap-3 rounded-xl border border-border bg-background px-4 py-4 md:px-5">
      <h2 id="proximos-passos" className="m-0 text-base font-semibold">{copy.onboarding}</h2>
      {!enabled ? (
        <p className="m-0 text-sm text-muted-foreground">{copy.onboarding_off}</p>
      ) : !onboarding ? (
        <p className="m-0 text-sm text-muted-foreground">{copy.onboarding_waiting_health}</p>
      ) : (
        <>
          <p className="m-0 text-sm">{statusText(onboarding, copy)}</p>
          {onboarding.last_error && <p role="alert" className="m-0 text-sm text-destructive">{onboarding.last_error}</p>}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
            {onboarding.atlas_task_url && (
              <Link href={onboarding.atlas_task_url} className="inline-flex items-center gap-1 font-medium text-primary hover:underline">
                {copy.open_atlas_task}<ArrowUpRight className="size-3.5" aria-hidden />
              </Link>
            )}
            {onboarding.bayes_task_url && (
              <Link href={onboarding.bayes_task_url} className="inline-flex items-center gap-1 font-medium text-primary hover:underline">
                {copy.open_bayes_task}<ArrowUpRight className="size-3.5" aria-hidden />
              </Link>
            )}
          </div>
          {onboarding.approved_at && (
            <p className="m-0 text-xs text-muted-foreground">
              {format(copy.approved_by, { name: onboarding.approved_by ?? '', date: formatDate(onboarding.approved_at, true) })}
            </p>
          )}
          {canManage && onboarding.approvable && approvePath && (
            <div className="grid gap-2 rounded-lg border border-border bg-muted/20 p-3">
              <p className="m-0 text-xs text-muted-foreground">{copy.approve_hint}</p>
              <div>
                <Button size="sm" onClick={approve} disabled={approving}>
                  <ShieldCheck className="size-4" aria-hidden />{approving ? copy.approving : copy.approve}
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  )
}
